import React, { useState } from "react";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { scrollToQuote } from "@/lib/scrollToQuote";

const iconFor = (name) => {
    const map = {
        car: Icons.Car,
        footprints: Icons.Footprints,
        sun: Icons.Sun,
        "layout-grid": Icons.LayoutGrid,
        "plus-square": Icons.PlusSquare,
        "minus-square": Icons.MinusSquare,
        hammer: Icons.Hammer,
        wrench: Icons.Wrench,
        truck: Icons.Truck,
        shovel: Icons.Shovel,
    };
    return map[name] || Icons.Hammer;
};

function ServiceCard({ service, span, index }) {
    const [active, setActive] = useState(0);
    const Icon = iconFor(service.icon);
    // Coerce to 3-photo array
    const photos = (service.image_urls && service.image_urls.length > 0
        ? service.image_urls
        : service.image_url
            ? [service.image_url]
            : []
    ).slice(0, 3);

    return (
        <div
            data-testid={`service-card-${index}`}
            className={`${span} group bg-[#0a0a0a] transition-colors relative overflow-hidden min-h-[420px] md:min-h-[480px] flex flex-col justify-end`}
        >
            {/* Photo stack — active photo fades in */}
            {photos.length > 0 ? (
                <>
                    {photos.map((src, i) => (
                        <img
                            key={i}
                            src={src}
                            alt={`${service.title} — example ${i + 1}`}
                            loading="lazy"
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                                i === active ? "opacity-70 group-hover:opacity-90" : "opacity-0"
                            } group-hover:scale-105`}
                            style={{ transition: "opacity 700ms ease, transform 900ms ease" }}
                        />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/10" />
                </>
            ) : (
                <div className="absolute inset-0 bg-[#0a0a0a]" />
            )}

            {/* Top row: icon + number */}
            <div className="relative z-10 p-6 md:p-8 flex items-start justify-between">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-black/70 backdrop-blur-sm border border-zinc-700 group-hover:border-yellow-400 group-hover:bg-yellow-400 flex items-center justify-center transition-colors">
                    <Icon className="w-5 h-5 md:w-6 md:h-6 text-white group-hover:text-black transition-colors" />
                </div>
                <span className="font-mono-accent text-[10px] text-white/60 bg-black/50 backdrop-blur-sm px-2 py-1">
                    {String(index + 1).padStart(2, "0")}
                </span>
            </div>

            {/* Bottom content */}
            <div className="relative z-10 p-6 md:p-8 pt-0">
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl text-white group-hover:text-yellow-400 transition-colors leading-none">
                    {service.title}
                </h3>
                <p className="mt-3 text-zinc-200 text-sm md:text-base leading-relaxed max-w-lg">
                    {service.description}
                </p>

                <div className="mt-6 flex items-center justify-between gap-4">
                    <span className="font-mono-accent text-[10px] md:text-[11px] text-yellow-400 flex-1">
                        {service.tagline}
                    </span>
                    <a
                        href="#quote"
                        onClick={scrollToQuote}
                        aria-label={`Request quote for ${service.title}`}
                        data-testid={`service-cta-${index}`}
                        className="w-10 h-10 shrink-0 border border-zinc-600 bg-black/60 backdrop-blur-sm group-hover:border-yellow-400 group-hover:bg-yellow-400 flex items-center justify-center transition-all"
                    >
                        <ArrowUpRight className="w-4 h-4 text-white group-hover:text-black" />
                    </a>
                </div>

                {/* Thumbnail strip — 3 photos */}
                {photos.length > 1 && (
                    <div className="mt-6 flex gap-2" data-testid={`service-thumbs-${index}`}>
                        {photos.map((src, i) => (
                            <button
                                key={i}
                                type="button"
                                onMouseEnter={() => setActive(i)}
                                onFocus={() => setActive(i)}
                                onClick={() => setActive(i)}
                                aria-label={`View ${service.title} photo ${i + 1}`}
                                data-testid={`service-thumb-${index}-${i}`}
                                className={`relative h-14 w-20 md:h-16 md:w-24 shrink-0 overflow-hidden border-2 transition-colors ${
                                    i === active
                                        ? "border-yellow-400"
                                        : "border-zinc-700 hover:border-zinc-500"
                                }`}
                            >
                                <img
                                    src={src}
                                    alt=""
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                                {i === active && (
                                    <span className="absolute inset-0 ring-1 ring-yellow-400/70" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Services({ services }) {
    if (!services || services.length === 0) return null;

    const spanClasses = [
        "md:col-span-3",
        "md:col-span-3",
        "md:col-span-2",
        "md:col-span-2",
        "md:col-span-2",
        "md:col-span-6",
        "md:col-span-3",
    ];

    return (
        <section id="services" className="relative bg-[#050505] border-t border-zinc-900">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-yellow-400" />
                            <span className="font-mono-accent text-[11px] text-yellow-400">
                                01 / Services
                            </span>
                        </div>
                        <h2
                            data-testid="services-heading"
                            className="font-display text-white text-5xl md:text-7xl lg:text-8xl"
                        >
                            What we <span className="text-yellow-400">pour</span>.
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                            Six specialties, one crew. Every project starts with a walk-through and a
                            written quote — no hidden fees, no surprise charges.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-zinc-900 border border-zinc-900">
                    {services.map((s, idx) => (
                        <ServiceCard
                            key={s.id || idx}
                            service={s}
                            index={idx}
                            span={spanClasses[idx] || "md:col-span-2"}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
